import React from "react";
import { Info, AlertCircle, Mail, Shield } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * CLARIAS Legal Disclaimers Component
 * Contains all mandatory disclaimer text and placement variants
 */

// 1. NO GUARANTEE DISCLAIMER (MANDATORY)
export const NoGuaranteeDisclaimer = ({ variant = "default" }) => {
  const text = "CLARIAS provides AI-based educational assistance. It does not guarantee selection, rank, marks, or exam results.";
  
  if (variant === "footer") {
    return (
      <p className="text-xs text-slate-400 leading-relaxed">
        {text}
      </p>
    );
  }
  
  if (variant === "pricing") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
        <p className="text-xs text-slate-500 leading-relaxed">
          <AlertCircle className="w-3 h-3 inline mr-1 -mt-0.5" />
          {text}
        </p>
      </div>
    );
  }
  
  if (variant === "onboarding") {
    return (
      <div className="flex items-start gap-2 text-xs text-slate-500">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>{text}</p>
      </div>
    );
  }
  
  // Default
  return (
    <p className="text-sm text-slate-600">
      {text}
    </p>
  );
};

// 2. AI OUTPUT DISCLAIMER (MANDATORY)
export const AIOutputDisclaimer = ({ variant = "default" }) => {
  const text = "All responses on CLARIAS are AI-generated and provided for guidance only. Users should apply their own judgement while preparing for exams.";
  
  if (variant === "tooltip") {
    return (
      <div className="group relative inline-flex items-center">
        <Info className="w-4 h-4 text-slate-400 cursor-help" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-64 z-50">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
        </div>
      </div>
    );
  }
  
  if (variant === "inline") {
    return (
      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
        <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          {text}
        </p>
      </div>
    );
  }
  
  if (variant === "small") {
    return (
      <p className="text-xs text-slate-400 flex items-center gap-1">
        <Info className="w-3 h-3" />
        AI-generated response. Apply your own judgement.
      </p>
    );
  }
  
  // Default
  return (
    <div className="text-sm text-slate-600">
      <p>{text}</p>
    </div>
  );
};

// 4. CUSTOMER SUPPORT / GRIEVANCE CONTACT (MANDATORY)
export const SupportContact = ({ variant = "default" }) => {
  if (variant === "footer") {
    return (
      <div className="text-xs text-slate-400">
        <p className="font-medium text-slate-500">Customer Support & Grievance Contact</p>
        <a href="mailto:support@clarias.in" className="hover:text-slate-300 transition-colors">
          support@clarias.in
        </a>
      </div>
    );
  }
  
  if (variant === "card") {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Mail className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">Customer Support & Grievance Contact</p>
            <a href="mailto:support@clarias.in" className="text-indigo-600 hover:underline text-sm">
              support@clarias.in
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  if (variant === "inline") {
    return (
      <p className="text-sm text-slate-600">
        For support or grievances, contact us at{" "}
        <a href="mailto:support@clarias.in" className="text-indigo-600 hover:underline font-medium">
          support@clarias.in
        </a>
      </p>
    );
  }
  
  // Default
  return (
    <div>
      <p className="font-medium text-slate-700">Customer Support & Grievance Contact</p>
      <p className="text-slate-600">Email: <a href="mailto:support@clarias.in" className="text-indigo-600 hover:underline">support@clarias.in</a></p>
    </div>
  );
};

// 5. COOKIE & ANALYTICS NOTICE
export const CookieNotice = ({ variant = "default" }) => {
  const text = "CLARIAS uses cookies and analytics tools to improve platform performance and user experience.";
  
  if (variant === "banner") {
    return (
      <div className="bg-slate-800 text-white px-4 py-3 text-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <p className="text-slate-300">
            {text}
          </p>
          <button className="px-4 py-1.5 bg-white text-slate-800 rounded-full text-xs font-medium hover:bg-slate-100 transition-colors shrink-0">
            Got it
          </button>
        </div>
      </div>
    );
  }
  
  // Default (for privacy policy)
  return (
    <p className="text-sm text-slate-600">
      {text}
    </p>
  );
};

// Combined Legal Footer (for use in app footer)
export const LegalFooterContent = () => {
  return (
    <div className="space-y-4">
      {/* No Guarantee Disclaimer */}
      <NoGuaranteeDisclaimer variant="footer" />
      
      {/* Support Contact */}
      <SupportContact variant="footer" />
      
      {/* Policy Links */}
      <div className="flex flex-wrap gap-3 text-xs">
        <Link to="/terms-of-service" className="text-slate-400 hover:text-slate-300 transition-colors">
          Terms of Service
        </Link>
        <span className="text-slate-600">|</span>
        <Link to="/privacy-policy" className="text-slate-400 hover:text-slate-300 transition-colors">
          Privacy Policy
        </Link>
        <span className="text-slate-600">|</span>
        <Link to="/refund-policy" className="text-slate-400 hover:text-slate-300 transition-colors">
          Refund Policy
        </Link>
      </div>
    </div>
  );
};

export default {
  NoGuaranteeDisclaimer,
  AIOutputDisclaimer,
  SupportContact,
  CookieNotice,
  LegalFooterContent
};
